'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

import { flywayMapGesture } from '@/config/flyway-map';

type GestureTransform = {
  x: number;
  y: number;
  scale: number;
};

type PointerSample = {
  id: number;
  x: number;
  y: number;
  pointerType: string;
};

const IDENTITY: GestureTransform = { x: 0, y: 0, scale: 1 };
const LENIS_PREVENT_ATTR = 'data-lenis-prevent';

function isMousePointer(pointerType: string): boolean {
  return pointerType === 'mouse';
}

function clampScale(scale: number): number {
  return Math.min(
    flywayMapGesture.maxScale,
    Math.max(flywayMapGesture.minScale, scale),
  );
}

function clampPan(x: number, y: number): { x: number; y: number } {
  const { maxPanLeft, maxPanRight, maxPanY } = flywayMapGesture;
  return {
    x: Math.min(maxPanRight, Math.max(-maxPanLeft, x)),
    y: Math.min(maxPanY, Math.max(-maxPanY, y)),
  };
}

function clampTransform(transform: GestureTransform): GestureTransform {
  const scale = clampScale(transform.scale);
  const pan = clampPan(transform.x, transform.y);
  return { scale, x: pan.x, y: pan.y };
}

function applyTransform(
  node: HTMLElement | null,
  transform: GestureTransform,
): void {
  if (!node) return;
  node.style.transform = `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`;
}

function distance(a: PointerSample, b: PointerSample): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

function midpoint(
  a: PointerSample,
  b: PointerSample,
): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function setLenisPrevent(host: HTMLElement, active: boolean): void {
  if (active) {
    host.setAttribute(LENIS_PREVENT_ATTR, '');
  } else {
    host.removeAttribute(LENIS_PREVENT_ATTR);
  }
}

type UseFlywaysMapGesturesOptions = {
  enabled: boolean;
  hostRef: RefObject<HTMLElement | null>;
  layerRef: RefObject<HTMLElement | null>;
};

type UseFlywaysMapGesturesResult = {
  reset: () => void;
  showTwoFingerTip: boolean;
  handlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  };
  style: CSSProperties;
};

/**
 * Compact-only map gestures via CSS transform.
 * Touch/pen: two-finger pan + pinch; one finger scrolls the page
 * (`touch-action: pan-y`) and past a movement threshold shows a tip.
 * Mouse: click-drag pans with one pointer (no tip). Lenis is prevented
 * while a map gesture is active. Native `touchmove` is non-passive so
 * two-finger gestures can `preventDefault()` and stop page scroll.
 */
export function useFlywaysMapGestures({
  enabled,
  hostRef,
  layerRef,
}: UseFlywaysMapGesturesOptions): UseFlywaysMapGesturesResult {
  const transformRef = useRef<GestureTransform>({ ...IDENTITY });
  const pointersRef = useRef<Map<number, PointerSample>>(new Map());
  const pinchStartRef = useRef<{
    distance: number;
    scale: number;
    midX: number;
    midY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const mousePanStartRef = useRef<{
    pointerX: number;
    pointerY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const oneFingerOriginRef = useRef<{ x: number; y: number } | null>(null);
  const tipArmedRef = useRef(false);
  /** Once the user pans with two fingers, suppress the tip for this page visit. */
  const hasUsedTwoFingerRef = useRef(false);
  const tipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabledRef = useRef(enabled);
  const layerRefLatest = useRef(layerRef);
  const [mapGestureActive, setMapGestureActive] = useState(false);
  const [showTwoFingerTip, setShowTwoFingerTip] = useState(false);

  useEffect(() => {
    layerRefLatest.current = layerRef;
  }, [layerRef]);

  const clearTipHideTimeout = useCallback(() => {
    if (tipHideTimeoutRef.current != null) {
      clearTimeout(tipHideTimeoutRef.current);
      tipHideTimeoutRef.current = null;
    }
  }, []);

  const hideTipImmediately = useCallback(() => {
    clearTipHideTimeout();
    tipArmedRef.current = false;
    setShowTwoFingerTip(false);
  }, [clearTipHideTimeout]);

  const scheduleTipHide = useCallback(() => {
    clearTipHideTimeout();
    tipHideTimeoutRef.current = setTimeout(() => {
      tipHideTimeoutRef.current = null;
      tipArmedRef.current = false;
      setShowTwoFingerTip(false);
    }, flywayMapGesture.twoFingerTipHideMs);
  }, [clearTipHideTimeout]);

  const syncPointersFromMap = useCallback(
    (host: HTMLElement) => {
      const pointers = [...pointersRef.current.values()];
      const twoFinger = pointers.length === 2;
      const mousePan =
        pointers.length === 1 && isMousePointer(pointers[0].pointerType);

      // Apply synchronously so the browser doesn't scroll during the React commit.
      const gestureActive = twoFinger || mousePan;
      host.style.touchAction = gestureActive ? 'none' : 'pan-y';
      setLenisPrevent(host, gestureActive);
      setMapGestureActive(gestureActive);

      if (twoFinger) {
        hasUsedTwoFingerRef.current = true;
        hideTipImmediately();
        mousePanStartRef.current = null;
        const [a, b] = pointers;
        const dist = distance(a, b);
        const mid = midpoint(a, b);
        const current = transformRef.current;
        pinchStartRef.current = {
          distance: dist,
          scale: current.scale,
          midX: mid.x,
          midY: mid.y,
          originX: current.x,
          originY: current.y,
        };
        oneFingerOriginRef.current = null;

        for (const pointer of pointers) {
          try {
            host.setPointerCapture(pointer.id);
          } catch {
            // Capture may already be held or unsupported.
          }
        }
        return;
      }

      pinchStartRef.current = null;

      if (pointers.length === 1) {
        const [pointer] = pointers;
        oneFingerOriginRef.current = { x: pointer.x, y: pointer.y };
        tipArmedRef.current = false;

        if (isMousePointer(pointer.pointerType)) {
          const current = transformRef.current;
          mousePanStartRef.current = {
            pointerX: pointer.x,
            pointerY: pointer.y,
            originX: current.x,
            originY: current.y,
          };
          try {
            host.setPointerCapture(pointer.id);
          } catch {
            // Capture may already be held or unsupported.
          }
        } else {
          mousePanStartRef.current = null;
        }
        return;
      }

      oneFingerOriginRef.current = null;
      mousePanStartRef.current = null;
    },
    [hideTipImmediately],
  );

  // Swallow native touch scrolling while two fingers are on the map.
  // Also detect one-finger drag for the tip (pointer events may cancel during scroll).
  useEffect(() => {
    if (!enabled) return;
    const node = hostRef.current;
    if (!node) return;

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length >= 2 || pointersRef.current.size >= 2) {
        event.preventDefault();
        return;
      }

      if (event.touches.length === 1 && oneFingerOriginRef.current) {
        const touch = event.touches.item(0);
        if (!touch) return;
        const moved = Math.hypot(
          touch.clientX - oneFingerOriginRef.current.x,
          touch.clientY - oneFingerOriginRef.current.y,
        );
        if (
          !hasUsedTwoFingerRef.current &&
          !tipArmedRef.current &&
          moved >= flywayMapGesture.oneFingerTipThresholdPx
        ) {
          tipArmedRef.current = true;
          clearTipHideTimeout();
          setShowTwoFingerTip(true);
        }
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length >= 2) {
        hasUsedTwoFingerRef.current = true;
        event.preventDefault();
        node.style.touchAction = 'none';
        setLenisPrevent(node, true);
        hideTipImmediately();
        return;
      }

      if (event.touches.length === 1 && !oneFingerOriginRef.current) {
        const touch = event.touches.item(0);
        if (touch) {
          oneFingerOriginRef.current = {
            x: touch.clientX,
            y: touch.clientY,
          };
        }
      }
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length === 0 && tipArmedRef.current) {
        // Pointer up may not fire if the browser cancelled the pointer for scroll.
        scheduleTipHide();
      }
    };

    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchstart', onTouchStart, { passive: false });
    node.addEventListener('touchend', onTouchEnd);
    node.addEventListener('touchcancel', onTouchEnd);
    return () => {
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [
    enabled,
    mapGestureActive,
    hostRef,
    clearTipHideTimeout,
    scheduleTipHide,
    hideTipImmediately,
  ]);

  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) {
      transformRef.current = { ...IDENTITY };
      applyTransform(layerRef.current, IDENTITY);
      pointersRef.current.clear();
      pinchStartRef.current = null;
      mousePanStartRef.current = null;
      oneFingerOriginRef.current = null;
      tipArmedRef.current = false;
      clearTipHideTimeout();
      if (hostRef.current) {
        setLenisPrevent(hostRef.current, false);
      }
    }
  }, [enabled, layerRef, clearTipHideTimeout, hostRef]);

  useEffect(() => {
    const node = hostRef.current;
    return () => {
      clearTipHideTimeout();
      if (node) {
        setLenisPrevent(node, false);
      }
    };
  }, [clearTipHideTimeout, hostRef]);

  const reset = useCallback(() => {
    transformRef.current = { ...IDENTITY };
    applyTransform(layerRefLatest.current.current, IDENTITY);
    pointersRef.current.clear();
    pinchStartRef.current = null;
    mousePanStartRef.current = null;
    oneFingerOriginRef.current = null;
    tipArmedRef.current = false;
    clearTipHideTimeout();
    setShowTwoFingerTip(false);
    setMapGestureActive(false);
    if (hostRef.current) {
      setLenisPrevent(hostRef.current, false);
    }
  }, [clearTipHideTimeout, hostRef]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabledRef.current) return;
      const host = event.currentTarget;
      pointersRef.current.set(event.pointerId, {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        pointerType: event.pointerType,
      });
      syncPointersFromMap(host);
    },
    [syncPointersFromMap],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabledRef.current) return;
      if (!pointersRef.current.has(event.pointerId)) return;

      pointersRef.current.set(event.pointerId, {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        pointerType: event.pointerType,
      });

      const pointers = [...pointersRef.current.values()];
      const layer = layerRefLatest.current.current;

      if (pointers.length === 2 && pinchStartRef.current) {
        const [a, b] = pointers;
        const start = pinchStartRef.current;
        const nextDist = distance(a, b);
        if (start.distance <= 0) return;
        const nextScale = clampScale(start.scale * (nextDist / start.distance));
        const mid = midpoint(a, b);
        transformRef.current = clampTransform({
          scale: nextScale,
          x: start.originX + (mid.x - start.midX),
          y: start.originY + (mid.y - start.midY),
        });
        applyTransform(layer, transformRef.current);
        return;
      }

      if (pointers.length === 1 && mousePanStartRef.current) {
        const start = mousePanStartRef.current;
        transformRef.current = clampTransform({
          scale: transformRef.current.scale,
          x: start.originX + (event.clientX - start.pointerX),
          y: start.originY + (event.clientY - start.pointerY),
        });
        applyTransform(layer, transformRef.current);
        return;
      }

      // Tip only for touch/pen — mouse click-drag pans instead.
      if (
        pointers.length === 1 &&
        oneFingerOriginRef.current &&
        !isMousePointer(event.pointerType)
      ) {
        const [pointer] = pointers;
        const origin = oneFingerOriginRef.current;
        const moved = Math.hypot(pointer.x - origin.x, pointer.y - origin.y);
        if (
          !hasUsedTwoFingerRef.current &&
          !tipArmedRef.current &&
          moved >= flywayMapGesture.oneFingerTipThresholdPx
        ) {
          tipArmedRef.current = true;
          clearTipHideTimeout();
          setShowTwoFingerTip(true);
        }
      }
    },
    [clearTipHideTimeout],
  );

  const endPointer = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!pointersRef.current.has(event.pointerId)) return;
      const host = event.currentTarget;
      const wasTwoFinger = pointersRef.current.size >= 2;
      const tipWasArmed = tipArmedRef.current;

      pointersRef.current.delete(event.pointerId);
      try {
        host.releasePointerCapture(event.pointerId);
      } catch {
        // Already released.
      }
      syncPointersFromMap(host);

      // After a one-finger drag that armed the tip, keep it readable briefly.
      if (!wasTwoFinger && pointersRef.current.size === 0 && tipWasArmed) {
        scheduleTipHide();
      }
    },
    [scheduleTipHide, syncPointersFromMap],
  );

  const gestureActive = enabled && mapGestureActive;

  return {
    reset,
    showTwoFingerTip: enabled && showTwoFingerTip,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
    },
    style: {
      touchAction: gestureActive ? 'none' : 'pan-y',
      cursor: enabled ? (gestureActive ? 'grabbing' : 'grab') : undefined,
    },
  };
}
